import type React from 'react';
import styled, { useTheme } from 'styled-components';
import { useIntl } from 'react-intl';

import { getGuideColors } from 'app/features/leads/components/guide/guide-colors';

import { GUIDE_STEP_COUNT, GUIDE_STEP_TITLE_IDS } from 'app/features/leads/components/guide/constants';
import { CheckGlyph, ChevronLeftSmall, ChevronRightSmall } from 'app/features/leads/components/guide/guide-icons';
import Button from 'app/ui/components/atoms/button';
import Typography from 'app/ui/components/atoms/typography';

type GuideFooterProps = {
  currentStep: number;
  onBack: () => void;
  onNext: () => void;
  onDone: () => void;
};

const Footer = styled.footer`
  display: grid;
  grid-template-columns: auto 1fr;
  grid-template-rows: auto auto;
  column-gap: ${({ theme: { spacing } }) => spacing(2)};
  row-gap: ${({ theme: { spacing } }) => spacing(3)};
  align-items: center;
  width: 100%;
  padding-top: ${({ theme: { spacing } }) => spacing(2)};
  flex-shrink: 0;

  ${({ theme: { media, spacing } }) =>
    media.medium(
      () => `
      grid-template-columns: auto auto 1fr;
      grid-template-rows: auto;
      row-gap: 0;
      column-gap: ${spacing(4)};
    `,
    )}
`;

const DotsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme: { spacing } }) => spacing(2)};
  grid-column: 1;
  grid-row: 1;
`;

const Dot = styled.span<{ $active: boolean }>`
  display: inline-block;
  border-radius: 9999px;
  background-color: ${({ $active, theme }) => ($active ? theme.semantic.accent_500 : theme.semantic.neutrals_600)};
  width: ${({ $active }) => ($active ? '1.3125rem' : '0.625rem')};
  height: ${({ $active }) => ($active ? '0.5625rem' : '0.625rem')};
`;

const PaginationLabel = styled(Typography)`
  grid-column: 2;
  grid-row: 1;
  justify-self: end;
`;

const ButtonsGroup = styled.div`
  display: flex;
  align-items: stretch;
  gap: ${({ theme: { spacing } }) => spacing(2)};
  grid-column: 1 / -1;
  grid-row: 2;
  width: 100%;

  ${({ theme: { media } }) =>
    media.medium(
      () => `
      grid-column: 3;
      grid-row: 1;
      width: auto;
      justify-self: end;
    `,
    )}
`;

const BackButtonIcon = styled.span`
  display: inline-flex;
  color: ${({ theme }) => theme.semantic.text_small_elements};
`;

const BackButtonWrapper = styled.div`
  flex: 1;
  min-width: 0;

  button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: ${({ theme: { spacing } }) => spacing(2)};
    width: 100%;
    color: ${({ theme }) => theme.semantic.text_small_elements};
  }

  ${({ theme: { media } }) =>
    media.medium(
      () => `
      flex: none;

      button {
        width: auto;
      }
    `,
    )}
`;

const NextButtonWrapper = styled.div`
  flex: 1;
  min-width: 0;

  button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: ${({ theme: { spacing } }) => spacing(2)};
    width: 100%;
  }

  ${({ theme: { media } }) =>
    media.medium(
      () => `
      flex: none;

      button {
        width: auto;
      }
    `,
    )}
`;

export const GuideFooter: React.FC<GuideFooterProps> = ({ currentStep, onBack, onNext, onDone }) => {
  const intl = useIntl();
  const { semantic } = useTheme();
  const guideColors = getGuideColors(semantic);
  const isFirst = currentStep === 0;
  const isLast = currentStep === GUIDE_STEP_COUNT - 1;

  const paginationLabel = intl.formatMessage(
    { id: 'leadsGuide.pagination' },
    { current: currentStep + 1, total: GUIDE_STEP_COUNT },
  );

  const backLabel = intl.formatMessage({ id: 'leadsGuide.buttons.back' });
  const nextLabel = intl.formatMessage({ id: 'leadsGuide.buttons.next' });
  const doneLabel = intl.formatMessage({ id: 'leadsGuide.buttons.done' });

  return (
    <Footer data-testid="leads-guide-footer">
      <DotsGroup aria-hidden={true}>
        {GUIDE_STEP_TITLE_IDS.map((stepId, index) => (
          <Dot key={stepId} $active={index === currentStep} />
        ))}
      </DotsGroup>
      <PaginationLabel variant="body3" color={guideColors.textSubtle} data-testid="leads-guide-pagination">
        {paginationLabel}
      </PaginationLabel>
      <ButtonsGroup>
        {!isFirst && (
          <BackButtonWrapper>
            <Button
              type="secondary-white"
              onClick={onBack}
              label={backLabel}
              icon={
                <BackButtonIcon>
                  <ChevronLeftSmall />
                </BackButtonIcon>
              }
              iconPosition="left"
              className="leads-guide-back"
            />
          </BackButtonWrapper>
        )}
        <NextButtonWrapper>
          <Button
            type="primary"
            onClick={isLast ? onDone : onNext}
            label={isLast ? doneLabel : nextLabel}
            icon={
              isLast ? (
                <span style={{ color: semantic.content_primary_button, display: 'inline-flex' }}>
                  <CheckGlyph size={16} />
                </span>
              ) : (
                <ChevronRightSmall />
              )
            }
            iconPosition="right"
            className={isLast ? 'leads-guide-done' : 'leads-guide-next'}
          />
        </NextButtonWrapper>
      </ButtonsGroup>
    </Footer>
  );
};
